import React, { useState, useCallback } from 'react';
import { ChromePicker } from 'react-color';
import { Pipette } from 'lucide-react';

const hexToRgb = (hex) => {
  const clean = (hex || '').replace('#', '');
  const full =
    clean.length === 3
      ? clean.split('').map((c) => c + c).join('')
      : clean;
  return {
    r: parseInt(full.substring(0, 2), 16),
    g: parseInt(full.substring(2, 4), 16),
    b: parseInt(full.substring(4, 6), 16),
    a: 1,
  };
};

// Wraps react-color's ChromePicker and adds a native eyedropper button so the
// user can sample a color from anywhere on screen. The picked color is shaped
// like react-color's onChange payload ({ hex, rgb }) so existing handlers work.
const ChromePickerWithEyeDropper = ({ onChange, onChangeComplete, ...props }) => {
  const [isPicking, setIsPicking] = useState(false);
  const isSupported =
    typeof window !== 'undefined' && 'EyeDropper' in window;

  const handlePickFromScreen = useCallback(async () => {
    if (!isSupported) return;
    try {
      setIsPicking(true);
      // eslint-disable-next-line no-undef
      const eyeDropper = new window.EyeDropper();
      const { sRGBHex } = await eyeDropper.open();
      const colorObj = { hex: sRGBHex, rgb: hexToRgb(sRGBHex), source: 'eyedropper' };
      if (onChange) onChange(colorObj);
      if (onChangeComplete) onChangeComplete(colorObj);
    } catch {
      // User dismissed the eyedropper — nothing to do.
    } finally {
      setIsPicking(false);
    }
  }, [isSupported, onChange, onChangeComplete]);

  return (
    <div className="chrome-picker-with-eyedropper">
      <ChromePicker onChange={onChange} onChangeComplete={onChangeComplete} {...props} />
      {isSupported && (
        <button
          type="button"
          onClick={handlePickFromScreen}
          disabled={isPicking}
          title="Pick a color from anywhere on screen"
          className="mt-2.5 mb-2.5 w-full flex items-center justify-center gap-2 px-3 py-[9px] text-sm font-normal text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300 transition-colors disabled:opacity-50"
        >
          <Pipette size={15} />
          {isPicking ? 'Picking…' : 'Pick from screen'}
        </button>
      )}
    </div>
  );
};

export default ChromePickerWithEyeDropper;
